import { Link, useLocation } from "react-router-dom";
import { BRAND } from "@/lib/constants";
import { Instagram, Facebook, Linkedin, Youtube, MessageCircle, Mail } from "lucide-react";

const QUICK_LINKS = [
  { label: "Home", to: { pathname: "/", hash: "#home" } },
  { label: "Services", to: { pathname: "/", hash: "#services" } },
  { label: "Industries", to: { pathname: "/", hash: "#industries" } },
  { label: "About", to: { pathname: "/", hash: "#about" } },
  { label: "Why Us", to: { pathname: "/", hash: "#why" } },
  { label: "Process", to: { pathname: "/", hash: "#process" } },
  { label: "FAQ", to: { pathname: "/", hash: "#faq" } },
  { label: "Privacy Policy", to: "/privacy-policy", route: true },
  { label: "Terms & Conditions", to: "/terms-and-conditions", route: true },
  { label: "Contact", to: { pathname: "/", hash: "#contact" } },
];

export const Footer = () => {
  const { pathname } = useLocation();

  return (
    <footer data-testid="footer" className="pt-20 pb-10 bg-[#02040A] border-t border-white/5 relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-4 gap-10">
          <div className="md:col-span-2">
            <Link to="/" className="flex items-center gap-3">
              <img src={BRAND.logo} alt="VR Marketing Hub" className="w-12 h-12 rounded-full ring-1 ring-[#D4AF37]/40" />
              <div>
                <div className="text-white font-bold tracking-tight">VR Marketing Hub</div>
                <div className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37]">Growth · Leads · Results</div>
              </div>
            </Link>
            <p className="text-white/55 text-sm mt-5 max-w-md leading-relaxed">
              AI-powered digital marketing agency built to help Indian businesses generate more leads, more sales and faster growth.
            </p>
            <div className="flex items-center gap-3 mt-6">
              {[
                { icon: MessageCircle, href: BRAND.whatsappLink, label: "WhatsApp", test: "footer-social-whatsapp" },
                { icon: Instagram, href: "#", label: "Instagram", test: "footer-social-instagram" },
                { icon: Facebook, href: "#", label: "Facebook", test: "footer-social-facebook" },
                { icon: Linkedin, href: "#", label: "LinkedIn", test: "footer-social-linkedin" },
                { icon: Youtube, href: "#", label: "YouTube", test: "footer-social-youtube" },
              ].map(({ icon: Icon, href, label, test }) => (
                <a key={label} href={href} data-testid={test} target="_blank" rel="noreferrer" aria-label={label} className="w-9 h-9 rounded-full grid place-items-center bg-white/5 border border-white/10 text-white/70 hover:text-[#D4AF37] hover:border-[#D4AF37]/40 transition-colors">
                  <Icon size={15} />
                </a>
              ))}
            </div>
          </div>

          <div>
            <div className="text-[11px] uppercase tracking-[0.28em] text-[#D4AF37] mb-4">Quick Links</div>
            <ul className="space-y-2.5 text-sm">
              {QUICK_LINKS.map((l) => {
                const testId = `footer-link-${l.label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`;
                // Route links (Privacy / Terms) always use <Link>
                if (l.route) {
                  return (
                    <li key={l.label}>
                      <Link to={l.to} data-testid={testId} className="text-white/60 hover:text-white transition-colors">
                        {l.label}
                      </Link>
                    </li>
                  );
                }
                // Landing-hash links: use <Link> so they navigate home from admin/legal pages
                return (
                  <li key={l.label}>
                    <Link
                      to={l.to}
                      data-testid={testId}
                      onClick={(e) => {
                        // When already on landing, smooth-scroll to the section
                        if (pathname === "/") {
                          const id = l.to.hash?.slice(1);
                          const el = id && document.getElementById(id);
                          if (el) {
                            e.preventDefault();
                            el.scrollIntoView({ behavior: "smooth", block: "start" });
                          }
                        }
                      }}
                      className="text-white/60 hover:text-white transition-colors"
                    >
                      {l.label}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>

          <div>
            <div className="text-[11px] uppercase tracking-[0.28em] text-[#D4AF37] mb-4">Get in Touch</div>
            <ul className="space-y-3 text-sm">
              <li>
                <a href={BRAND.whatsappLink} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-white/70 hover:text-white">
                  <MessageCircle size={14} className="text-emerald-400" /> {BRAND.phone}
                </a>
              </li>
              <li>
                <a href={`mailto:${BRAND.email}`} className="flex items-center gap-2 text-white/70 hover:text-white break-all">
                  <Mail size={14} className="text-[#D4AF37]" /> {BRAND.email}
                </a>
              </li>
              <li className="text-white/50 text-xs pt-2">India · Serving all states</li>
            </ul>
          </div>
        </div>

        <div className="mt-14 pt-6 border-t border-white/5 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="text-xs text-white/45">© {new Date().getFullYear()} VR Marketing Hub. All rights reserved.</div>
          <div className="text-xs text-white/45">Made in India · For Indian Businesses</div>
        </div>
      </div>
    </footer>
  );
};
